import React, { useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error("Veuillez accepter les CGU et la politique RGPD.");
    if (password.length < 8) return toast.error("Mot de passe: 8 caractères minimum.");
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Compte créé. Veuillez compléter votre qualification.");
      nav("/qualification");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur à la création du compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="label-divider mb-6">{t.nav.signup}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900" data-testid="register-title">
            {t.auth.signup_title}
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 block">{t.auth.name}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" data-testid="register-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 block">{t.auth.email}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" data-testid="register-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 block">{t.auth.password}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-sm" data-testid="register-password" />
              <div className="text-[11px] text-slate-500 mt-1">8 caractères minimum.</div>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={terms} onCheckedChange={(v) => setTerms(Boolean(v))} data-testid="register-terms" className="mt-0.5" />
              <span>{t.auth.terms}</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11" data-testid="register-submit">
              {loading ? "…" : t.auth.submit_signup}
            </Button>
          </form>

          <div className="my-6 label-divider">{t.auth.or}</div>

          <Button variant="outline" onClick={googleLogin} className="w-full rounded-sm h-11 border-slate-300" data-testid="google-signup-btn">
            <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            {t.auth.google}
          </Button>

          <div className="mt-8 text-sm text-slate-600">
            {t.auth.have_account}{" "}
            <Link to="/login" className="text-slate-900 font-semibold link-underline" data-testid="go-login">
              {t.nav.login}
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate opacity-20" />
        <div className="relative p-16 h-full flex flex-col justify-between text-white">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-400 font-bold">Onboarding investisseur</div>
            <div className="mt-6 font-display text-4xl font-bold leading-tight max-w-md">
              "Votre espace, vos documents, votre télémétrie GPU en temps réel."
            </div>
            <ul className="mt-10 space-y-3 text-sm text-slate-300">
              <li>1. Création de compte et qualification investisseur</li>
              <li>2. Choix du SPV et simulation</li>
              <li>3. Paiement sécurisé et suivi de dossier</li>
              <li>4. Coupons semestriels capitalisés et reporting mensuel</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
